// src/services/api/appointmentAPI.js
import { createAuthHeaders } from '../../utils/authUtils';

const FIREBASE_FUNCTIONS_BASE_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_BASE_URL;

/**
 * Client-side API service for appointment operations
 * Calls Firebase Functions instead of direct database access
 */
class AppointmentAPI {
  /**
   * Get appointments by date range
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @returns {Promise<Array>} Array of appointments
   */
  static async getByDateRange(startDate, endDate) {
    try {
      console.log('API: Getting appointments by date range', { startDate, endDate });

      // Validate parameters
      if (!startDate || !endDate) {
        throw new Error('Both startDate and endDate are required');
      }

      // Use retry mechanism for this request
      return await this._retryRequest(async () => {
        // Construct URL with query parameters
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/appointments`);
        url.searchParams.set('operation', 'getByDateRange');
        url.searchParams.set('startDate', startDate);
        url.searchParams.set('endDate', endDate);

        console.log('API: Making request to:', url.toString());

        // Make the API call
        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });

        // Check if response is ok
        if (!response.ok) {
          await this._handleAPIError(response, 'getByDateRange');
        }

        // Parse response
        const result = await response.json();

        // Check for API-level errors
        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Successfully retrieved appointments', {
          startDate,
          endDate,
          count: result.data?.length || 0
        });

        return result.data || [];
      });

    } catch (error) {
      console.error('API Error in getByDateRange:', {
        startDate,
        endDate,
        error: error.message,
        stack: error.stack
      });

      // Re-throw with more context
      throw new Error(`Failed to fetch appointments by date range: ${error.message}`);
    }
  }

static async create(appointmentData) {
  console.log('API: Creating new appointment', { appointmentData });

  if (!appointmentData) {
    throw new Error('appointmentData is required');
  }

  return await this._retryRequest(async () => {
    const url = `${FIREBASE_FUNCTIONS_BASE_URL}/appointments`;
    const response = await fetch(url, {
      method: 'POST',
      headers: await createAuthHeaders(),
      body: JSON.stringify({
        operation: 'create',
        appointmentData: appointmentData
      })
    });

    // This throws error with errorCode from _handleAPIError
    if (!response.ok) {
      await this._handleAPIError(response, 'create');
    }

    const result = await response.json();

    if (!result.success) {
      const err = new Error(result.message || result.error || 'API request was not successful');
      err.errorCode = result.errorCode;
      err.details = result.details;
      throw err;
    }

    return result.data;
  });
}

  /**
   * Get appointment by ID
   * @param {string} appointmentId - Appointment ID
   * @returns {Promise<Object|null>} Appointment data or null if not found
   */
  static async getById(appointmentId) {
    try {
      console.log('API: Getting appointment by ID', { appointmentId });

      // Validate parameters
      if (!appointmentId) {
        throw new Error('appointmentId is required');
      }

      // Validate UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(appointmentId)) {
        throw new Error('Invalid appointmentId format');
      }

      // Use retry mechanism for this request
      return await this._retryRequest(async () => {
        // Construct URL with query parameters
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/appointments`);
        url.searchParams.set('operation', 'getById');
        url.searchParams.set('appointmentId', appointmentId);

        console.log('API: Making request to:', url.toString());

        // Make the API call
        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });

        // Check if response is ok
        if (!response.ok) {
          await this._handleAPIError(response, 'getById');
        }

        // Parse response
        const result = await response.json();

        // Check for API-level errors
        if (!result.success) {
          const err = new Error(result.message || result.error || 'API request was not successful');
          err.errorCode = result.errorCode;
          err.details = result.details;
          throw err;
        }

        // Handle case where appointment is not found (result.data is null)
        if (result.data === null) {
          console.log('API: Appointment not found', { appointmentId });
          return null;
        }

        console.log('API: Successfully retrieved appointment by ID', {
          appointmentId: result.data?.id,
          customerName: result.data?.customer_name,
          date: result.data?.date,
          time: result.data?.time,
          status: result.data?.status
        });

        return result.data;
      });

    } catch (error) {
      console.error('API Error in getById:', {
        appointmentId,
        error: error.message,
        stack: error.stack
      });

      // Re-throw with more context
      throw new Error(`Failed to get appointment by ID: ${error.message}`);
    }
  }

  /**
   * Delete appointment (hard delete)
   * @param {string} appointmentId - Appointment ID to delete
   * @returns {Promise<Object>} Deletion result
   */
  static async delete(appointmentId, deleteMode = 'single') {
    try {
      console.log('API: Deleting appointment', { appointmentId, deleteMode });

      // Validate parameters
      if (!appointmentId) {
        throw new Error('appointmentId is required');
      }

      // Use retry mechanism for this request
      return await this._retryRequest(async () => {
        // Construct URL with query parameters
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/appointments`);
        url.searchParams.set('operation', 'delete');
        url.searchParams.set('appointmentId', appointmentId);
        url.searchParams.set('deleteMode', deleteMode);

        console.log('API: Making DELETE request to:', url.toString());

        // Make the API call
        const response = await fetch(url.toString(), {
          method: 'GET', // Using GET with operation parameter as per existing pattern
          headers: await createAuthHeaders(),
        });

        // Check if response is ok
        if (!response.ok) {
          await this._handleAPIError(response, 'delete');
        }

        // Parse response
        const result = await response.json();

        // Check for API-level errors
        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Successfully deleted appointment', {
          appointmentId: result.data?.appointmentId,
          customerName: result.data?.deletedAppointment?.customer_name,
          date: result.data?.deletedAppointment?.date,
          time: result.data?.deletedAppointment?.time
        });

        return result.data;
      });

    } catch (error) {
      console.error('API Error in delete:', {
        appointmentId,
        error: error.message,
        stack: error.stack
      });

      // Re-throw with more context
      throw new Error(`Failed to delete appointment: ${error.message}`);
    }
  }

  static async update(appointmentId, updates) {
    try {
      console.log('API: Updating appointment', { appointmentId, updates });

      // Validate parameters
      if (!appointmentId) {
        throw new Error('appointmentId is required');
      }

      if (!updates || Object.keys(updates).length === 0) {
        throw new Error('updates object is required and cannot be empty');
      }

      // Use retry mechanism for this request
      return await this._retryRequest(async () => {
        // Construct URL with query parameters
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/appointments`);
        url.searchParams.set('operation', 'update');
        url.searchParams.set('appointmentId', appointmentId);

        console.log('API: Making PUT request to:', url.toString());

        // Make the API call
        const response = await fetch(url.toString(), {
          method: 'POST', // Using POST with operation parameter as per existing pattern
          headers: await createAuthHeaders(),
          body: JSON.stringify({
            operation: 'update',
            updates: updates
          })
        });

        // Check if response is ok
        if (!response.ok) {
          await this._handleAPIError(response, 'update');
        }

        // Parse response
        const result = await response.json();

        // Check for API-level errors
        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Successfully updated appointment', {
          appointmentId: result.data?.id,
          customerName: result.data?.customer_name,
          date: result.data?.date,
          time: result.data?.time,
          updatedFields: Object.keys(updates)
        });

        return result.data;
      });

    } catch (error) {
      console.error('API Error in update:', {
        appointmentId,
        updates,
        error: error.message,
        stack: error.stack
      });

      // Re-throw with more context
      throw new Error(`Failed to update appointment: ${error.message}`);
    }
  }

  static async getOverdueAppointments() {
    try {
      console.log('API: Getting overdue appointments');

      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/appointments`);
        url.searchParams.set('operation', 'getOverdue');

        console.log('API: Making request to:', url.toString());

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'getOverdueAppointments');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Successfully retrieved overude appointments', {
          count: result.data?.length || 0,
          date: new Date().toISOString().split('T')[0]
        });

        return result.data || [];
      });

    } catch (error) {
      console.error('API Error in getOverdueAppointments:', {
        error: error.message,
        stack: error.stack
      });

      throw new Error(`Failed to get today's appointments: ${error.message}`);
    }
  }

  static async getUnpaidAppointments() {
    try {
      console.log('API: Getting unpaid appointments');

      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/appointments`);
        url.searchParams.set('operation', 'getUnpaid');

        console.log('API: Making request to:', url.toString());

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'getUnpaidAppointments');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Successfully retrieved unpaid appointments', {
          count: result.data?.length || 0,
          date: new Date().toISOString().split('T')[0]
        });

        return result.data || [];
      });

    } catch (error) {
      console.error('API Error in getUnpaidAppointments:', {
        error: error.message,
        stack: error.stack
      });

      throw new Error(`Failed to get unpaid appointments: ${error.message}`);
    }
  }

  /**
   * Get today's appointments
   * @returns {Promise<Array>} Today's appointments
   */
  static async getTodayAppointments() {
    try {
      console.log('API: Getting today\'s appointments');

      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/appointments`);
        url.searchParams.set('operation', 'getTodayAppointments');

        console.log('API: Making request to:', url.toString());

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'getTodayAppointments');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Successfully retrieved today\'s appointments', {
          count: result.data?.length || 0,
          date: new Date().toISOString().split('T')[0]
        });

        return result.data || [];
      });

    } catch (error) {
      console.error('API Error in getTodayAppointments:', {
        error: error.message,
        stack: error.stack
      });

      throw new Error(`Failed to get today's appointments: ${error.message}`);
    }
  }

  /**
   * Get upcoming appointments
   * @param {number} limit - Number of appointments to return (default: 0 for no limit, max: 100)
   * @returns {Promise<Array>} Upcoming appointments
   */
  static async getUpcomingAppointments(limit = 0) {
    try {
      console.log('API: Getting upcoming appointments', {
        limit: limit === 0 ? 'unlimited' : limit
      });

      // Validate limit (0 means no limit)
      if (typeof limit !== 'number' || limit < 0 || limit > 100) {
        throw new Error('Limit must be a number between 0 and 100 (0 means no limit)');
      }

      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/appointments`);
        url.searchParams.set('operation', 'getUpcomingAppointments');
        url.searchParams.set('limit', limit.toString());

        console.log('API: Making request to:', url.toString());

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'getUpcomingAppointments');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Successfully retrieved upcoming appointments', {
          limit: limit === 0 ? 'unlimited' : limit,
          count: result.data?.length || 0
        });

        return result.data || [];
      });

    } catch (error) {
      console.error('API Error in getUpcomingAppointments:', {
        limit: limit === 0 ? 'unlimited' : limit,
        error: error.message,
        stack: error.stack
      });

      throw new Error(`Failed to get upcoming appointments: ${error.message}`);
    }
  }

  /**
   * Get dashboard metrics
   * @returns {Promise<Object>} Dashboard metrics
   */
  static async getDashboardMetrics() {
    try {
      console.log('API: Getting dashboard metrics');

      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/appointments`);
        url.searchParams.set('operation', 'getDashboardMetrics');

        console.log('API: Making request to:', url.toString());

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'getDashboardMetrics');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Successfully retrieved dashboard metrics', {
          todayAppointments: result.data?.today?.total_appointments,
          weekRevenue: result.data?.this_week?.revenue,
          monthRevenue: result.data?.this_month?.revenue
        });

        return result.data;
      });

    } catch (error) {
      console.error('API Error in getDashboardMetrics:', {
        error: error.message,
        stack: error.stack
      });

      throw new Error(`Failed to get dashboard metrics: ${error.message}`);
    }
  }

  /**
   * Get customer appointment history
   * @param {string} customerId - Customer ID
   * @param {number} limit - Number of appointments to return (default: 0 for no limit, max: 200)
   * @returns {Promise<Array>} Customer appointments
   */
  static async getCustomerAppointments(customerId, limit = 0) {
    try {
      console.log('API: Getting customer appointments', {
        customerId,
        limit: limit === 0 ? 'unlimited' : limit
      });

      // Validate parameters
      if (!customerId) {
        throw new Error('customerId is required');
      }

      // Validate UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(customerId)) {
        throw new Error('Invalid customerId format');
      }

      // Validate limit (0 means no limit)
      if (typeof limit !== 'number' || limit < 0 || limit > 200) {
        throw new Error('Limit must be a number between 0 and 200 (0 means no limit)');
      }

      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/appointments`);
        url.searchParams.set('operation', 'getCustomerAppointments');
        url.searchParams.set('customerId', customerId);
        url.searchParams.set('limit', limit.toString());

        console.log('API: Making request to:', url.toString());

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'getCustomerAppointments');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Successfully retrieved customer appointments', {
          customerId,
          limit: limit === 0 ? 'unlimited' : limit,
          count: result.data?.length || 0,
          customerName: result.data?.[0]?.customer_name || 'Unknown'
        });

        return result.data || [];
      });

    } catch (error) {
      console.error('API Error in getCustomerAppointments:', {
        customerId,
        limit: limit === 0 ? 'unlimited' : limit,
        error: error.message,
        stack: error.stack
      });

      throw new Error(`Failed to get customer appointments: ${error.message}`);
    }
  }

  static async _handleAPIError(response, operation) {
    try {
      const data = await response.json();
      const err = new Error(data.message || data.error || 'Unknown error');
      err.errorCode = data.errorCode;
      err.details = data.details;
      throw err;
    } catch (jsonError) {
      if (jsonError.errorCode) {
        throw jsonError; // Already formatted, re-throw
      }
      throw new Error(`${operation} failed: HTTP ${response.status} - ${response.statusText}`);
    }
  }

  /**
   * Generic retry mechanism for failed requests
   * @private
   * @param {Function} requestFn - Function that makes the actual request
   * @param {number} maxRetries - Maximum number of retry attempts
   * @param {number} delay - Initial delay between retries in milliseconds
   * @returns {Promise<*>} Result of the request function
   */
  static async _retryRequest(requestFn, maxRetries = 1, delay = 1000) {
    let lastError;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = error;

        // Don't retry on validation errors (4xx)
        if (error.message.includes('400') || error.message.includes('404') || error.message.includes('401')) {
          console.log('API: Not retrying due to client error:', error.message);
          throw error;
        }

        // If this is the last attempt, throw the error
        if (attempt === maxRetries) {
          console.log('API: Max retries reached, throwing error');
          throw error;
        }

        // Wait before retrying
        console.log(`API: Retrying request in ${delay}ms (attempt ${attempt + 1}/${maxRetries + 1})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        delay *= 2; // Exponential backoff
      }
    }

    throw lastError;
  }

  /**
   * Test API connectivity
   * @returns {Promise<boolean>} True if API is accessible
   */
  static async testConnection() {
    try {
      console.log('API: Testing connection...');
      const today = new Date().toISOString().split('T')[0];
      await this.getByDateRange(today, today);
      console.log('API: Connection test successful');
      return true;
    } catch (error) {
      console.error('API: Connection test failed:', error.message);
      return false;
    }
  }

  /**
   * Get the base URL for Firebase Functions
   * @returns {string} Base URL
   */
  static getBaseURL() {
    return FIREBASE_FUNCTIONS_BASE_URL;
  }

  /**
   * Check if the API is configured correctly
   * @returns {boolean} True if configuration looks valid
   */
  static isConfigured() {
    return !!FIREBASE_FUNCTIONS_BASE_URL && FIREBASE_FUNCTIONS_BASE_URL.includes('cloudfunctions.net');
  }

  /**
   * Create a timeout wrapper for requests
   * @private
   * @param {Function} requestFn - Function to wrap
   * @param {number} timeoutMs - Timeout in milliseconds
   * @returns {Promise<*>} Promise that resolves or rejects based on timeout
   */
  static async _withTimeout(requestFn, timeoutMs = 30000) {
    return Promise.race([
      requestFn(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error(`Request timeout after ${timeoutMs}ms`)), timeoutMs)
      )
    ]);
  }

  // Add this function to the AppointmentAPI class in appointmentAPI.js

  /**
   * Get monthly appointments summary for chart display
   * @returns {Promise<Array>} Array of {date, value} objects for chart
   */
  static async getMonthlyAppointmentsSummary() {
    try {
      console.log('API: Getting monthly appointments summary');

      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/appointments`);
        url.searchParams.set('operation', 'getMonthlyAppointmentsSummary');

        console.log('API: Making request to:', url.toString());

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'getMonthlyAppointmentsSummary');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Successfully retrieved monthly appointments summary', {
          monthsCount: result.data?.length || 0,
          totalAppointments: result.data?.reduce((sum, month) => sum + month.value, 0) || 0
        });

        return result.data || [];
      });

    } catch (error) {
      console.error('API Error in getMonthlyAppointmentsSummary:', {
        error: error.message,
        stack: error.stack
      });

      throw new Error(`Failed to get monthly appointments summary: ${error.message}`);
    }
  }

  /**
 * Get available appointment slots for a service
 * @param {string} serviceId - Service ID
 * @param {string} startDate - Start date (YYYY-MM-DD) 
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {Object} options - Additional options
 * @returns {Promise<Array>} Array of available slots by date
 */
  static async getAvailableSlots(serviceId, startDate, endDate, options = {}) {
    try {
      console.log('API: Getting available slots', { serviceId, startDate, endDate, options });

      // Validate parameters
      if (!serviceId || !startDate || !endDate) {
        throw new Error('serviceId, startDate, and endDate are required');
      }

      // Use retry mechanism for this request
      return await this._retryRequest(async () => {
        // Construct URL with query parameters
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/appointments`);
        url.searchParams.set('operation', 'getAvailableSlots');
        url.searchParams.set('serviceId', serviceId);
        url.searchParams.set('startDate', startDate);
        url.searchParams.set('endDate', endDate);
        url.searchParams.set('timezone', timezone);

        // Add optional parameters
        if (options.crewMemberId) {
          url.searchParams.set('crewMemberId', options.crewMemberId);
        }
        if (options.duration) {
          url.searchParams.set('duration', options.duration.toString());
        }
        if (options.timeSlotInterval) {
          url.searchParams.set('timeSlotInterval', options.timeSlotInterval.toString());
        }

        console.log('API: Making request to:', url.toString());

        // Make the API call
        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });

        // Check if response is ok
        if (!response.ok) {
          await this._handleAPIError(response, 'getAvailableSlots');
        }

        // Parse response
        const result = await response.json();

        // Check for API-level errors
        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Successfully retrieved available slots', {
          serviceId,
          startDate,
          endDate,
          datesWithSlots: result.data?.filter(day => day.totalSlots > 0).length || 0,
          totalSlots: result.data?.reduce((sum, day) => sum + day.totalSlots, 0) || 0
        });

        return result.data || [];
      });

    } catch (error) {
      console.error('API Error in getAvailableSlots:', {
        serviceId,
        startDate,
        endDate,
        options,
        error: error.message,
        stack: error.stack
      });

      // Re-throw with more context
      throw new Error(`Failed to get available slots: ${error.message}`);
    }
  }

  static async getPendingApprovals(limit = 50) {
    try {
      console.log('API: Getting pending approval appointments', { limit });

      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/appointments`);
        url.searchParams.set('operation', 'getPendingApprovals');
        url.searchParams.set('limit', limit.toString());

        console.log('API: Making request to:', url.toString());

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'getPendingApprovals');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Successfully retrieved pending appointments', {
          count: result.data?.length || 0
        });

        return result.data || [];
      });

    } catch (error) {
      console.error('API Error in getPendingApprovals:', {
        limit,
        error: error.message,
        stack: error.stack
      });
      throw new Error(`Failed to get pending appointments: ${error.message}`);
    }
  }

  /**
   * Get monthly service distribution for stacked area chart
   * @returns {Promise<Array>} Array of monthly service distribution data
   */
  static async getMonthlyServiceDistribution() {
    try {
      console.log('API: Getting monthly service distribution');

      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/appointments`);
        url.searchParams.set('operation', 'getMonthlyServiceDistribution');

        console.log('API: Making request to:', url.toString());

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'getMonthlyServiceDistribution');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Successfully retrieved monthly service distribution', {
          monthsCount: [...new Set(result.data?.map(row => row.month) || [])].length,
          servicesCount: [...new Set(result.data?.map(row => row.service_name) || [])].length,
          totalRecords: result.data?.length || 0
        });

        return result.data || [];
      });

    } catch (error) {
      console.error('API Error in getMonthlyServiceDistribution:', {
        error: error.message,
        stack: error.stack
      });

      throw new Error(`Failed to get monthly service distribution: ${error.message}`);
    }
  }

  /**
   * Get monthly revenue summary for a date range
   * @param {string} startMonth - Start month in YYYY-MM format
   * @param {string} endMonth - End month in YYYY-MM format
   * @returns {Promise<Array>} Array of monthly revenue data
   */
  static async getMonthlyRevenueSummary(startMonth, endMonth) {
    try {
      console.log('API: Getting monthly revenue summary', {
        startMonth,
        endMonth
      });

      // Validate parameters
      if (!startMonth || !endMonth) {
        throw new Error('Both startMonth and endMonth are required');
      }

      // Validate month format (YYYY-MM)
      const monthRegex = /^\d{4}-\d{2}$/;
      if (!monthRegex.test(startMonth) || !monthRegex.test(endMonth)) {
        throw new Error('Months must be in YYYY-MM format');
      }

      // Use retry mechanism for this request
      return await this._retryRequest(async () => {
        // Construct URL with query parameters
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/appointments`);
        url.searchParams.set('operation', 'getMonthlyRevenueSummary');
        url.searchParams.set('startMonth', startMonth);
        url.searchParams.set('endMonth', endMonth);

        console.log('API: Making request to:', url.toString());

        // Make the API call
        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });

        // Check if response is ok
        if (!response.ok) {
          await this._handleAPIError(response, 'getMonthlyRevenueSummary');
        }

        // Parse response
        const result = await response.json();

        // Check for API-level errors
        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Successfully retrieved monthly revenue summary', {
          startMonth,
          endMonth,
          monthsCount: result.data?.length || 0,
          totalRevenue: result.data?.reduce((sum, month) => sum + month.revenue, 0) || 0,
          totalUnpaid: result.data?.reduce((sum, month) => sum + month.unpaid, 0) || 0
        });

        return result.data || [];
      });

    } catch (error) {
      console.error('API Error in getMonthlyRevenueSummary:', {
        startMonth,
        endMonth,
        error: error.message,
        stack: error.stack
      });

      // Re-throw with more context
      throw new Error(`Failed to fetch monthly revenue summary: ${error.message}`);
    }
  }

  /**
   * Update appointment with invoice information
   * @param {string} appointmentId - Appointment ID
   * @param {Object} invoiceData - Invoice information
   * @param {string} invoiceData.invoiceUrl - URL to the invoice file in Firebase Storage
   * @param {string} invoiceData.invoiceNumber - Invoice number (optional)
   * @param {string} invoiceData.transactionId - Transaction ID (optional)
   * @param {string} invoiceData.processId - Process ID (optional)
   * @returns {Promise<Object>} Updated appointment
   */
  static async updateAppointmentInvoice(appointmentId, invoiceData) {
    try {
      console.log('API: Updating appointment with invoice', {
        appointmentId,
        hasInvoiceUrl: !!invoiceData.invoiceUrl
      });

      // Validate parameters
      if (!appointmentId) {
        throw new Error('appointmentId is required');
      }

      if (!invoiceData || !invoiceData.invoiceUrl) {
        throw new Error('invoiceData with invoiceUrl is required');
      }

      // Use retry mechanism for this request
      return await this._retryRequest(async () => {
        // Construct URL
        const url = `${FIREBASE_FUNCTIONS_BASE_URL}/appointments`;

        console.log('API: Making POST request to update appointment invoice');

        // Make the API call
        const response = await fetch(url, {
          method: 'POST',
          headers: await createAuthHeaders(),
          body: JSON.stringify({
            operation: 'updateAppointmentInvoice',
            appointmentId,
            invoiceData
          })
        });

        // Check if response is ok
        if (!response.ok) {
          await this._handleAPIError(response, 'updateAppointmentInvoice');
        }

        // Parse response
        const result = await response.json();

        // Check for API-level errors
        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Successfully updated appointment with invoice', {
          appointmentId: result.data?.id,
          hasInvoiceUrl: !!result.data?.invoice_url
        });

        return result.data;
      });

    } catch (error) {
      console.error('API Error in updateAppointmentInvoice:', {
        appointmentId,
        error: error.message,
        stack: error.stack
      });

      // Re-throw with more context
      throw new Error(`Failed to update appointment with invoice: ${error.message}`);
    }
  }
}

export { AppointmentAPI };